import { Box } from "@mui/material";
import { disabledBlockSx } from "../disabledStyles";
import {
  SelectedTagsByCategory,
  TagSelector,
  buildTagSmEntry,
  parseTagSmEntries,
  tagSmHasCategory,
} from "@platformx/tag-selector";
import { frameDocumentType, ShowToastError } from "@platformx/utilities";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import HiddenTextBox from "../HiddenTextBox/HiddenTextBox";

interface TagSelectorFieldProps {
  field: any;
  category?: string;
  subCategory?: string;
  isEdit?: boolean;
  selectedTag?: string[];
  register: any;
  error: any;
  state: any;
  setState: any;
  clearErrors: any;
  isDisabled?: boolean;
}

const MAX_SELECTED_TAGS = 15;

// A piece of content's type is fixed by the content itself (this schema's own content type),
// not something the author picks as a tag - hide these categories from the picker entirely.
// Any pre-existing tag_sm entry for them (e.g. the auto-selected content-type tag seeded below)
// is untouched; this only affects what TagSelector renders.
const HIDDEN_TAG_CATEGORIES = ["Content Type", "Collection"];

// Bridges the flat `state[field.name]` (tag names) + `state.tag_sm` (JSON-stringified
// {category, name, value} entries, read by mandatory-category validation elsewhere in this
// schema - see buildTagSmEntry/parseTagSmEntries in @platformx/utilities) contract that
// builder.tsx's "tag" field type reads/writes, onto @platformx/tag-selector's <TagSelector />,
// which tracks selection as { [category]: {name, value}[] } internally.
export const TagSelectorField = (props: TagSelectorFieldProps) => {
  const { t } = useTranslation();
  const {
    field,
    category: rawCategory = "",
    subCategory = "",
    selectedTag = [],
    register,
    error,
    state,
    setState,
    clearErrors,
    isDisabled = false,
  } = props;
  // Callers pass this straight from the URL/schema's raw document-type-ish value (e.g.
  // "mobilesetting", "tournamentsetting", "collection") - normalize through the same helper
  // the rest of the app uses for that value, so two routes for what's really the same backend
  // bucket don't split the same tag across two different tag_sm categories.
  const category = frameDocumentType(rawCategory);

  // Seeded once at mount — builder.tsx only renders the "tag" field once
  // `Object.keys(state).length > 0`, so pre-existing tag_sm data (edit mode) is
  // already present by the time TagSelector needs its initial selection.
  const defaultSelected: SelectedTagsByCategory = useMemo(() => {
    const entries = parseTagSmEntries(state?.tag_sm);
    const fromEntries = entries.reduce((acc: SelectedTagsByCategory, entry) => {
      const existing = acc[entry.category] || [];
      // A content item's tag_sm can already carry duplicate entries from an earlier save (see
      // handleChange's own dedup below) - skip re-adding one so it doesn't render as repeated
      // identical chips.
      if (existing.some((tag) => tag.value === entry.value)) return acc;
      acc[entry.category] = [...existing, { name: entry.name, value: entry.value }];
      return acc;
    }, {});

    // The caller's own category/tag (e.g. this content type's "Content Type" tag) must always
    // be present - both for brand-new content (mirroring TagListingComponent's
    // contentTypeCreateTagHandle) and for existing content saved before this tag existed or
    // worked, so editing + saving self-heals it instead of leaving it permanently missing.
    if (category && subCategory) {
      const existingForCategory = fromEntries[category] || [];
      if (!existingForCategory.some((tag) => tag.value === subCategory)) {
        return {
          ...fromEntries,
          [category]: [...existingForCategory, { name: subCategory, value: subCategory }],
        };
      }
    }
    return fromEntries;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateMandatoryCategories = () => {
    const mandatory: string[] = field?.mandatory_tag_category || [];
    if (mandatory.length === 0) return true;
    const missing = mandatory.filter(
      (mandatoryCategory) => !tagSmHasCategory(state?.tag_sm, mandatoryCategory),
    );
    return missing.length === 0
      ? true
      : `${field?.validations?.[0]?.message} ${t("category")}- ${field?.mandatory_tag_category}`;
  };

  const handleChange = (selected: SelectedTagsByCategory) => {
    const flatTags: string[] = [];
    const tagSm: string[] = [];
    // Shared across every category (not reset per category) - two categories can legitimately
    // select the same tag value (e.g. this content type's own auto-tag landing under both
    // "Content Type" and some other category), and tag_sm keeps one entry per (category, value)
    // pair, but the flat tags list is just a plain name list - it must not carry the same value
    // twice even when two different categories both selected it.
    const seenValues = new Set<string>();
    const seenTagSmEntries = new Set<string>();
    Object.entries(selected).forEach(([tagCategory, tags]) => {
      tags.forEach((tag) => {
        const tagSmKey = `${tagCategory}\u0000${tag.value}`;
        if (!seenTagSmEntries.has(tagSmKey)) {
          seenTagSmEntries.add(tagSmKey);
          tagSm.push(buildTagSmEntry(tagCategory, tag.name, tag.value));
        }
        if (seenValues.has(tag.value)) return;
        seenValues.add(tag.value);
        flatTags.push(tag.name);
      });
    });

    if (flatTags.length > MAX_SELECTED_TAGS) {
      ShowToastError(t("allowed_tags_toast"));
      return;
    }

    clearErrors(field?.name);
    setState({
      ...state,
      tag_sm: tagSm,
      tags: flatTags,
    });
  };

  // Persists the pre-checked/backfilled category tag into state right away - mirrors the old
  // TagListingComponent's `!isEdit && updateState(...)` mount effect for brand-new content, and
  // additionally self-heals existing content saved before this tag existed or worked: its own
  // defaultSelected chip renders pre-checked above, but a save without touching any chip would
  // otherwise never persist it.
  useEffect(() => {
    if (!category || !subCategory) return;
    const alreadyPersisted = parseTagSmEntries(state?.tag_sm).some(
      (entry) => entry.category === category && entry.value === subCategory,
    );
    if (!alreadyPersisted) {
      handleChange(defaultSelected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Some schemas name this "tag" field "tags", others "tag_sm" (see builder.tsx's
  // selectedTag special-case) - render for either, not just the literal "tags" name.
  const isTagsField = field?.name === "tags" || field?.name === "tag_sm";

  return (
    <Box
      className='noSpace'
      sx={{ backgroundColor: "#ffffff", marginBottom: "12px", ...disabledBlockSx(isDisabled) }}>
      <Box sx={{ display: isTagsField ? "block" : "none" }}>
        <HiddenTextBox
          error={error}
          value={selectedTag || ""}
          register={register}
          name={field?.name}
          required={
            field?.validations?.length > 0 &&
            field?.validations?.[0]?.type === "required" &&
            !selectedTag?.length
              ? field?.validations[0]?.message
              : ""
          }
          validate={validateMandatoryCategories}
        />
      </Box>
      <Box sx={{ display: isTagsField ? "block" : "none" }}>
        <TagSelector
          defaultSelected={defaultSelected}
          onChange={handleChange}
          hiddenCategories={HIDDEN_TAG_CATEGORIES}
        />
      </Box>
    </Box>
  );
};

export default TagSelectorField;
